<template>
  <div>
    <header-bar v-if="isBtoB">支払履歴</header-bar>
    <header-bar v-if="isBtoC">支払い申請</header-bar>
    <div class="payment-block">
      <div class="page-block__heading page-block__heading--withbar">
        <h3
          v-if="isBtoB"
          class="page-block__title">
          お支払い履歴一覧
        </h3>
        <h3
          v-if="isBtoC"
          class="page-block__title">
          お支払い
        </h3>
      </div>
      <div
        v-if="subscriptionDefaulted"
        class="payment-error">
        <p>
          配信し放題プランのお支払いの確認ができておりません。<br>
          確認できるまでは支払い申請ができませんので、ご了承ください。<br>
          決済情報の更新をお願いいたします。
        </p>
        <div class="c-btn-wrap">
          <router-link
            :to="{name: 'settlement-method-change'}"
            class="c-btn">
            決済情報を更新する
            <span class="c-btn__icon-arrow"></span>
          </router-link>
        </div>
      </div>

      <div
        v-if="subscriptionCarrierFeePending"
        class="subscription-carrier-fee-pending-block">
        <h2>【重要なお知らせ】</h2>
        <p
          class="subscription-carrier-fee-pending-notification">
          配信し放題プランの料金改定に伴い、下記の期限までに決済情報の再登録が必要となります。<br>
          <br>
          <em>期限：2024年 2月29日 23時59分</em><br>
          <br>
          決済情報の再登録が完了するまで、支払い申請を含む一部のサービスをご利用いただくことができません。<br>
          お手数ですが、下記の「決済情報を更新する」より<span>決済情報の再登録をお願いいたします。</span><br>
          <br>
          期限内に決済情報の再登録が完了しない場合、楽曲の配信停止および退会の手続きを取らせていただきます。<br>
          退会に伴いまして、<span>残高に計上されている収益をお支払することができなくなります。</span><br>
          ご理解の程、よろしくお願い申し上げます。<br>
        </p>
        <div class="c-btn-wrap">
          <router-link
            :to="{name: 'settlement-method-change'}"
            class="c-btn">
            決済情報を更新する
            <span class="c-btn__icon-arrow"></span>
          </router-link>
        </div>
      </div>
      <div class="payment-block__inner">
        <div
          v-if="isBtoC"
          class="payment-about">
          <balance-report
            button-title="支払い申請する"
            :subscription-carrier-fee-pending="subscriptionCarrierFeePending"></balance-report>
          <div class="payment-about__block">
            <div class="payment-about__text pcmt20">
              収益状況一覧となります。<br />
              収益のお引き出しをご希望の場合は「支払い申請する」をクリックして支払い申請手続きを行ってください。<br />
              支払スケジュールは毎月月末締めの翌月末日にお支払いとなります。
            </div>
            <div class="payment-about__text--caution">
              ※銀行休業日の場合は、前営業日となります。<br />
              ※{{
                minimumTransferPrice | roundNumber
              }}円以上から支払い申請が可能となります。
            </div>
          </div>
        </div>
        <div class="c-table-caption">横にスクロールしてご覧ください</div>
        <div class="payment-detail-list">
          <table class="payment-detail-list__table">
            <thead class="payment-detail-list__thead">
              <tr>
                <th class="payment-detail-list__cell-date">日付</th>
                <th class="payment-detail-list__cell-type">内容</th>
                <th class="payment-detail-list__cell-money">金額</th>
                <th
                  v-if="isBtoC"
                  class="payment-detail-list__cell-balance">
                  収益残高
                </th>
                <th class="payment-detail-list__cell-status">
                  お支払いステータス
                </th>
                <th class="payment-detail-list__cell-note">備考</th>
              </tr>
            </thead>
            <tbody
              v-for="trade in trades"
              :key="trade.index"
              class="payment-detail-list__tbody">
              <tr>
                <td>
                  <template v-if="trade.transfer_date">
                    <!-- 振込済みならば振込日を表示、振込済みで無いならば作成日を表示 -->
                    {{ trade.status===1 ? trade.transfer_date:trade.created_at | date }}
                  </template>
                  <template v-else>
                    {{ trade.created_at | date }}
                  </template>
                </td>
                <td>
                  {{ trade.title }}
                </td>
                <td class="u-text-align-right">
                  ¥<span v-if="trade.type <= 0 && isBtoC">-</span>
                  {{ trade.money | roundNumber }}
                </td>
                <td
                  v-if="isBtoC"
                  class="u-text-align-right">
                  ¥{{ trade.balance | roundNumber }}
                </td>
                <td class="u-text-align-center">
                  {{ trade.status_name }}
                </td>
                <td class="u-text-align-center">
                  {{ trade.note }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment'
import { mapGetters } from 'vuex'
import HeaderBar from '../HeaderBar'
import BalanceReport from '../Top/Dashboard/BalanceReport'

export default {
  components: {
    HeaderBar,
    BalanceReport,
  },
  filters: {
    date(value) {
      if (!value) {
        return ''
      }
      return moment(
        new Date(value.length === 10 ? value : value.substr(0, 10)),
      ).format('YYYY年MM月DD日')
    },
    roundNumber(value) {
      return value
        ? new Intl.NumberFormat('ja-JP').format(Math.round(value * 100) / 100)
        : 0
    },
  },
  data() {
    return {
      trades: [],
    }
  },
  computed: {
    minimumTransferPrice() {
      // 最低振込金額
      return this.$store.state.auth.company.is_consumer === 0 ? 10000 : 1000
    },
    ...mapGetters('auth', ['subscriptionDefaulted', 'isSubscriber', 'subscriptionCarrierFeePending']),
  },
  mounted() {
    this.fetchTrade()
  },
  methods: {
    async fetchTrade() {
      const { data } = await window.axios.get('/api/client/payments/trades')
      this.trades = data
    },
  },
}
</script>
<style scoped>
@media screen and (min-width: 896px) {
  .subscription-carrier-fee-pending-block{
      max-width: 1080px;
  }
}
@media screen and (max-width: 895px) {
  .subscription-carrier-fee-pending-block{
    width:95%;
  }
}
</style>
